'use client'

import { motion } from 'framer-motion'
import { Zap, Star, Shield, TrendingUp } from 'lucide-react'
import { AnimatedCounter } from '../ui/AnimatedCounter'

interface SocialProofBannerProps {
  variant?: 'compact' | 'expanded'
  animated?: boolean
}

const stats = [
  {
    icon: Zap,
    numericValue: 150,
    suffix: '+',
    label: 'Entregues com resultados',
    shortLabel: 'projetos'
  },
  {
    icon: Star,
    numericValue: 5.0,
    suffix: '',
    label: 'Avaliação média',
    shortLabel: 'nota',
    isDecimal: true
  },
  {
    icon: Shield,
    numericValue: 98,
    suffix: '%',
    label: 'Satisfação',
    shortLabel: 'felizes'
  }
]

export function SocialProofBanner({ variant = 'compact', animated = true }: SocialProofBannerProps) {
  if (variant === 'compact') {
    return (
      <motion.div
        initial={animated ? { opacity: 0, y: -10 } : false}
        animate={{ opacity: 1, y: 0 }}
        className="px-3 sm:px-4 py-2 bg-white/85 dark:bg-white/5 backdrop-blur-xl border border-purple-500/20 dark:border-white/10 rounded-full"
      >
        <div className="flex items-center justify-center gap-3 sm:gap-4 text-[10px] sm:text-xs">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={stat.label}
                initial={animated ? { opacity: 0, scale: 0.8 } : false}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-1 sm:gap-1.5"
              >
                <Icon size={10} className="sm:w-3 sm:h-3 text-purple-600" />
                <span className="font-semibold text-purple-600">
                  {animated ? (
                    <AnimatedCounter
                      end={stat.numericValue}
                      suffix={stat.suffix}
                      duration={1.5}
                    />
                  ) : (
                    `${stat.numericValue}${stat.suffix}`
                  )}
                </span>
                <span className="text-indigo-950/70 dark:text-white/70 hidden sm:inline">{stat.shortLabel}</span>
              </motion.div>
            )
          })}
        </div>
      </motion.div>
    )
  }

  // Variant expanded - para desktop
  return (
    <motion.div
      initial={animated ? { opacity: 0, x: 20 } : false}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 }}
      className="p-4 border-b border-purple-500/20 dark:border-white/10"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={16} className="text-purple-500 dark:text-purple-600" />
        <span className="text-sm font-medium text-indigo-950/80 dark:text-white/80">Por que escolher a Biomo?</span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.label}
              initial={animated ? { opacity: 0, y: 10 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="flex flex-col items-center p-3 rounded-xl bg-white/85 dark:bg-white/5 backdrop-blur-xl border border-purple-500/20 dark:border-white/10"
            >
              <div className="flex items-center gap-1.5 text-purple-500 dark:text-purple-600 mb-1">
                <Icon size={16} />
                <span className="text-lg font-bold">
                  {animated ? (
                    <AnimatedCounter
                      end={stat.numericValue}
                      suffix={stat.suffix}
                      duration={1.8}
                    />
                  ) : (
                    `${stat.numericValue}${stat.suffix}`
                  )}
                </span>
              </div>
              <span className="text-[10px] text-indigo-950/70 dark:text-white/70 text-center leading-tight">
                {stat.label}
              </span>
            </motion.div>
          )
        })}
      </div>

      {/* Trust message */}
      <motion.p
        initial={animated ? { opacity: 0 } : false}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="mt-4 text-xs text-indigo-950/60 dark:text-white/60 text-center"
      >
        Junte-se a centenas de empresas que confiam na Biomo
      </motion.p>
    </motion.div>
  )
}
